import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Text,
  Textarea,
  VStack,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { useBounty, updateBounty } from '../../hooks/useBounties';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { RESEARCH_TYPES } from '../../utils/formatters';

export default function EditBounty() {
  const { bountyId } = useParams();
  const { currentUser } = useAuth();
  const { bounty, loading } = useBounty(bountyId);
  const navigate = useNavigate();
  const toast = useToast();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [researchType, setResearchType] = useState('');
  const [rewardAmount, setRewardAmount] = useState('');
  const [saving, setSaving] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  // Pre-populate once bounty loads
  useEffect(() => {
    if (bounty) {
      setTitle(bounty.title || '');
      setDescription(bounty.description || '');
      setResearchType(bounty.researchType || '');
      setRewardAmount(bounty.rewardAmount ? String(bounty.rewardAmount / 100) : '');
    }
  }, [bounty?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <LoadingSpinner message="Loading task..." />;

  if (!bounty) {
    return (
      <Container maxW="2xl" py={8} textAlign="center">
        <Heading size="md" mb={4}>Task not found</Heading>
        <Button as={Link} to="/my-tasks" leftIcon={<Icon as={FiArrowLeft} />}>My Tasks</Button>
      </Container>
    );
  }

  if (currentUser?.uid !== bounty.creatorUid) {
    return (
      <Container maxW="2xl" py={8} textAlign="center">
        <Heading size="md" mb={4}>You don't have permission to edit this task</Heading>
        <Button as={Link} to={`/tasks/${bountyId}`} leftIcon={<Icon as={FiArrowLeft} />}>Back to Task</Button>
      </Container>
    );
  }

  if (bounty.status !== 'open') {
    return (
      <Container maxW="2xl" py={8} textAlign="center">
        <Heading size="md" mb={2}>This task can no longer be edited</Heading>
        <Text color="gray.500" mb={4}>Only open tasks can be edited.</Text>
        <Button as={Link} to={`/tasks/${bountyId}`} leftIcon={<Icon as={FiArrowLeft} />}>Back to Task</Button>
      </Container>
    );
  }

  const isValid = title.trim().length >= 5 && description.trim().length >= 20 && researchType && parseFloat(rewardAmount) >= 100;

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      await updateBounty(bountyId, {
        title: title.trim(),
        description: description.trim(),
        researchType,
        rewardAmount: Math.round(parseFloat(rewardAmount) * 100),
      });
      toast({
        title: 'Task updated',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      navigate(`/tasks/${bountyId}`);
    } catch (err) {
      toast({ title: 'Error saving changes', description: err.message, status: 'error', duration: 5000, isClosable: true });
      setSaving(false);
    }
  };

  return (
    <Container maxW="2xl" py={8}>
      <VStack spacing={6} align="stretch">
        <Button
          as={Link}
          to={`/tasks/${bountyId}`}
          variant="ghost"
          leftIcon={<Icon as={FiArrowLeft} />}
          alignSelf="flex-start"
          size="sm"
        >
          Back to Task
        </Button>

        <Box>
          <Heading size="lg">Edit Task</Heading>
          <Text color="gray.600" mt={1}>Changes apply immediately once saved.</Text>
        </Box>

        <Box bg={cardBg} rounded="xl" p={8} boxShadow="sm" borderWidth={1} borderColor={borderColor}>
          <VStack spacing={6} align="stretch">
            <FormControl isRequired>
              <FormLabel>Task Title</FormLabel>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                size="lg"
              />
              <FormHelperText>{title.length}/100 characters (min 5)</FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Research Type</FormLabel>
              <Select
                value={researchType}
                onChange={(e) => setResearchType(e.target.value)}
                size="lg"
              >
                <option value="">Select research type</option>
                {Object.entries(RESEARCH_TYPES).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={6}
                resize="vertical"
              />
              <FormHelperText>{description.length} characters (min 20)</FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Reward Amount (USD)</FormLabel>
              <NumberInput
                min={100}
                max={50000}
                value={rewardAmount}
                onChange={(val) => setRewardAmount(val)}
                size="lg"
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText>Minimum $100</FormHelperText>
            </FormControl>
          </VStack>
        </Box>

        <HStack justify="flex-end">
          <Button
            as={Link}
            to={`/tasks/${bountyId}`}
            variant="ghost"
            size="lg"
          >
            Cancel
          </Button>
          <Button
            colorScheme="orange"
            size="lg"
            isDisabled={!isValid}
            isLoading={saving}
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </HStack>
      </VStack>
    </Container>
  );
}
